/**
 * NotificationPanel — értesítés dropdown a Topbarban
 * - Olvasatlanok felül, olvasottak "Korábbi értesítések" alatt (auto-betöltve)
 * - Kattintásra detail modal nyílik a teljes üzenettel
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, del } from '../lib/api';
import { usePush } from '../hooks/usePush';

interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link_url?: string | null;
  link?: string | null;
  is_read: boolean;
  created_at: string;
}

interface Props {
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'most';
  if (diff < 3600) return `${Math.floor(diff / 60)} perce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} órája`;
  return `${Math.floor(diff / 86400)} napja`;
}

function fmtFull(iso: string): string {
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const KIND_ICON: Record<string, string> = {
  'network.invite': '🤝',
  'network.accepted': '✅',
  'network.declined': '❌',
  'network.price_share': '🎯',
  'network.order_received': '📦',
  'network.order_confirmed': '✅',
  'network.order_rejected': '❌',
  'network.order_fulfilled': '🚚',
  'network.invoice_received': '🧾',
  'task.assigned': '📋',
  'task.due_soon': '⏰',
  'task.overdue': '🔴',
  'invoice.overdue': '💸',
  'nav.submission_failed': '⚠️',
};

// ── Detail modal ─────────────────────────────────────────────────────────────

function NotifDetailModal({ n, onClose, onNavigate }: {
  n: Notification;
  onClose: () => void;
  onNavigate: (url: string) => void;
}) {
  const link = n.link_url || n.link;
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(20,35,63,0.45)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'var(--paper)',
        borderRadius: '16px 16px 0 0',
        width: '100%', maxWidth: 480,
        padding: '20px 20px 32px',
        boxShadow: '0 -8px 32px rgba(20,35,63,.25)',
        maxHeight: '80vh', overflowY: 'auto',
      }}>
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />
        </div>

        {/* Icon + title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 28, flexShrink: 0 }}>{KIND_ICON[n.kind] || '🔔'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)', lineHeight: 1.4 }}>{n.title}</div>
            <div style={{ fontSize: 11, color: 'var(--slate-2)', marginTop: 3 }}>{fmtFull(n.created_at)}</div>
          </div>
        </div>

        {/* Body */}
        {n.body && (
          <div style={{
            fontSize: 14, color: 'var(--navy)', lineHeight: 1.6,
            background: 'var(--mist)', borderRadius: 10, padding: '12px 14px',
            marginBottom: 20, whiteSpace: 'pre-wrap',
          }}>
            {n.body}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          {link && (
            <button
              onClick={() => { onNavigate(link); onClose(); }}
              style={{
                flex: 1, padding: '11px', background: 'var(--orange)', color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Megnyitás →
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              flex: link ? 0 : 1, padding: '11px 20px',
              background: 'var(--mist)', color: 'var(--navy)',
              border: '1px solid var(--line)', borderRadius: 10, fontSize: 14,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Bezárás
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fő panel ─────────────────────────────────────────────────────────────────

export default function NotificationPanel({ onClose, onCountChange }: Props) {
  const [unread, setUnread] = useState<Notification[]>([]);
  const [older, setOlder] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Notification | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const navigate = useNavigate();
  const { pushState, subscribe, loading: pushLoading } = usePush();

  const load = useCallback(async () => {
    const [unreadResult, olderResult] = await Promise.allSettled([
      get<Notification[]>('/notifications?is_read=false&pageSize=50'),
      get<Notification[]>('/notifications?is_read=true&pageSize=50'),
    ]);
    const u = unreadResult.status === 'fulfilled' && Array.isArray(unreadResult.value) ? unreadResult.value : [];
    const o = olderResult.status === 'fulfilled' && Array.isArray(olderResult.value) ? olderResult.value : [];
    setUnread(u);
    setOlder(o);
    onCountChangeRef.current?.(u.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Valós idejű frissítés 15s-onként
  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click (csak ha nincs detail modal nyitva)
  useEffect(() => {
    if (selected) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = 'touches' in e ? e.touches[0]?.target : (e as MouseEvent).target;
      if (panelRef.current && !panelRef.current.contains(target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler as EventListener);
    document.addEventListener('touchstart', handler as EventListener);
    return () => {
      document.removeEventListener('mousedown', handler as EventListener);
      document.removeEventListener('touchstart', handler as EventListener);
    };
  }, [onClose, selected]);

  const markRead = async (id: string) => {
    try {
      await post(`/notifications/${id}/mark-read`, {});
      setUnread(prev => {
        const item = prev.find(n => n.id === id);
        if (item) setOlder(o => [{ ...item, is_read: true }, ...o]);
        return prev.filter(n => n.id !== id);
      });
      onCountChange?.(Math.max(0, unread.length - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await post('/notifications/mark-all-read', {});
      setOlder(o => [...unread.map(n => ({ ...n, is_read: true })), ...o]);
      setUnread([]);
      onCountChange?.(0);
    } catch { /* silent */ }
  };

  const remove = async (id: string, isUnread: boolean) => {
    try {
      await del(`/notifications/${id}`);
      if (isUnread) {
        setUnread(prev => prev.filter(n => n.id !== id));
        onCountChange?.(Math.max(0, unread.length - 1));
      } else {
        setOlder(prev => prev.filter(n => n.id !== id));
      }
    } catch { /* silent */ }
  };

  const openDetail = (n: Notification) => {
    if (!n.is_read) markRead(n.id);
    setSelected(n);
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  const containerStyle: React.CSSProperties = isMobile ? {
    position: 'fixed', left: 0, right: 0, bottom: 0, top: 'auto',
    width: '100%', maxHeight: '82vh',
    background: 'var(--paper)', borderTop: '1px solid var(--line)',
    borderRadius: '16px 16px 0 0', boxShadow: '0 -8px 32px rgba(20,35,63,.18)',
    zIndex: 200,
  } : {
    position: 'absolute', right: 0, top: '48px', width: '380px',
    maxHeight: '600px', background: 'var(--paper)',
    border: '1px solid var(--line)', borderRadius: 'var(--r)',
    boxShadow: '0 18px 40px -14px rgba(20,35,63,.35)',
    zIndex: 200,
  };

  const scrollStyle: React.CSSProperties = isMobile ? {
    maxHeight: 'calc(82vh - 120px)', overflowY: 'auto',
  } : {
    maxHeight: 'calc(600px - 110px)', overflowY: 'auto',
  };

  const NotifItem = ({ n, isUnread }: { n: Notification; isUnread: boolean }) => (
    <div
      onClick={() => openDetail(n)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 14px', borderBottom: '1px solid var(--line-2)',
        background: isUnread ? 'rgba(244,123,32,0.05)' : 'transparent',
        cursor: 'pointer',
        transition: 'background .1s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = isUnread ? 'rgba(244,123,32,0.10)' : 'var(--mist)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = isUnread ? 'rgba(244,123,32,0.05)' : 'transparent'; }}
    >
      <div style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
        {KIND_ICON[n.kind] || '🔔'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: isUnread ? 600 : 400, fontSize: 13, color: 'var(--navy)', marginBottom: 2 }}>{n.title}</div>
        {n.body && (
          <div style={{
            fontSize: 12, color: 'var(--slate)', lineHeight: 1.4, marginBottom: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {n.body}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--slate-2)' }}>{timeAgo(n.created_at)}</div>
      </div>
      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        {isUnread && (
          <button
            onClick={e => { e.stopPropagation(); markRead(n.id); }}
            title="Olvasottnak jelöl"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', fontSize: 13, color: 'var(--slate)' }}
          >✓</button>
        )}
        <button
          onClick={e => { e.stopPropagation(); remove(n.id, isUnread); }}
          title="Törlés"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', fontSize: 13, color: 'var(--slate)' }}
        >×</button>
      </div>
    </div>
  );

  const SectionHeader = ({ label }: { label: string }) => (
    <div style={{
      padding: '6px 14px', fontSize: 10, fontWeight: 700, color: 'var(--slate-2)',
      textTransform: 'uppercase', letterSpacing: '.08em', background: 'var(--mist)',
      position: 'sticky', top: 0, zIndex: 1,
    }}>
      {label}
    </div>
  );

  return (
    <>
      <div ref={panelRef} style={containerStyle}>
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px', flexShrink: 0 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', flex: 1 }}>
            Értesítések
            {unread.length > 0 && (
              <span style={{ background: 'var(--orange)', color: '#fff', borderRadius: 10, fontSize: 11, padding: '1px 6px', marginLeft: 6 }}>
                {unread.length}
              </span>
            )}
          </span>
          {unread.length > 0 && (
            <button onClick={markAllRead}
              style={{ fontSize: 11, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
              Mind olvasott
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div style={scrollStyle}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--slate)' }}>Betöltés…</div>
          ) : unread.length === 0 && older.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--slate)' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
              Nincs értesítésed
            </div>
          ) : (
            <>
              {/* Olvasatlanok */}
              {unread.length > 0 ? (
                <>
                  <SectionHeader label={`Új — ${unread.length} db`} />
                  {unread.map(n => <NotifItem key={n.id} n={n} isUnread={true} />)}
                </>
              ) : (
                <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--slate)', textAlign: 'center' }}>
                  Nincs olvasatlan értesítésed 👍
                </div>
              )}

              {/* Korábban elolvasottak */}
              {older.length > 0 && (
                <>
                  <SectionHeader label={`Korábban — ${older.length} db`} />
                  {older.map(n => <NotifItem key={n.id} n={n} isUnread={false} />)}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', background: 'var(--mist)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          {pushState === 'subscribed' ? (
            <span style={{ fontSize: 12, color: 'var(--slate)', flex: 1 }}>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>●</span> Push bekapcsolva
            </span>
          ) : pushState === 'denied' ? (
            <span style={{ fontSize: 12, color: 'var(--slate)', flex: 1 }}>Push tiltva</span>
          ) : pushState === 'unsupported' || pushState === 'ios_old' || pushState === 'ios_not_installed' ? (
            <span style={{ fontSize: 12, color: 'var(--slate)', flex: 1 }}>Push nem támogatott</span>
          ) : (
            <button onClick={subscribe} disabled={pushLoading}
              style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flex: 1 }}>
              {pushLoading ? 'Kapcsolódás…' : '🔔 Push bekapcsolása'}
            </button>
          )}
          <button onClick={() => { onClose(); navigate('/beallitasok/ertesitesek'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--slate)', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            Beállítások →
          </button>
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <NotifDetailModal
          n={selected}
          onClose={() => setSelected(null)}
          onNavigate={url => { navigate(url); onClose(); }}
        />
      )}
    </>
  );
}
